import React from "react";
import { FilterButton } from "./FilterButton";
import { RangeSlider } from "../../../ui";
import { ChartBarIcon } from "@heroicons/react/20/solid";
import type { ScoreFilter as ScoreFilterType } from "../types";

export interface ScoreFilterProps {
  ref: React.RefObject<HTMLDivElement>;
  isExpanded: boolean;
  onToggle: () => void;
  minScore: number;
  maxScore: number;
  onSliderChange: (values: number[]) => void;
  onApply: () => void;
  onCancel: () => void;
  onRemove: () => void;
  isActive: boolean;
  canApply: boolean;
  currentLabel: string;
  sliderMin: number;
  sliderMax: number;
  sliderStep: number;
}

/**
 * Format score range for display in filter button label.
 */
export function formatScoreRangeLabel(scoreFilter?: ScoreFilterType): string {
  if (!scoreFilter || (scoreFilter.min_score === undefined && scoreFilter.max_score === undefined)) {
    return "All";
  }
  if (scoreFilter.min_score !== undefined && scoreFilter.max_score !== undefined) {
    if (scoreFilter.min_score === scoreFilter.max_score) {
      return scoreFilter.min_score.toFixed(2);
    }
    return `${scoreFilter.min_score.toFixed(2)} - ${scoreFilter.max_score.toFixed(2)}`;
  }
  if (scoreFilter.min_score !== undefined) {
    return `≥ ${scoreFilter.min_score.toFixed(2)}`;
  }
  if (scoreFilter.max_score !== undefined) {
    return `≤ ${scoreFilter.max_score.toFixed(2)}`;
  }
  return "All";
}

/**
 * Filter component for selecting score range (0-1) via dual-handle slider.
 */
export const ScoreFilter = React.forwardRef<
  HTMLDivElement,
  Omit<ScoreFilterProps, "ref">
>(
  (
    {
      isExpanded,
      onToggle,
      minScore,
      maxScore,
      onSliderChange,
      onApply,
      onCancel,
      onRemove,
      isActive,
      canApply,
      currentLabel,
      sliderMin,
      sliderMax,
      sliderStep,
    },
    ref
  ) => {
    return (
      <div ref={ref} className="relative">
        <FilterButton
          label={`Score: ${currentLabel}`}
          isActive={isActive}
          isExpanded={isExpanded}
          onClick={onToggle}
          onRemove={isActive ? onRemove : undefined}
          icon={<ChartBarIcon className="h-4 w-4" />}
        />

        {isExpanded && (
          <div className="absolute bg-card border border-border rounded-lg shadow-lg p-4 z-10 mt-1 min-w-[280px]">
            <div className="mb-4">
              <RangeSlider
                label={`Score range: ${minScore.toFixed(2)} - ${maxScore.toFixed(2)}`}
                min={sliderMin}
                max={sliderMax}
                step={sliderStep}
                value={[minScore, maxScore]}
                onValueChange={onSliderChange}
                size="sm"
                color="primary"
              />
            </div>

            <div className="flex gap-2 pt-3 border-t">
              <button
                onClick={onCancel}
                className="flex-1 px-3 py-2 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
              >
                Cancel
              </button>
              <button
                onClick={onApply}
                disabled={!canApply}
                className="flex-1 px-3 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:bg-muted disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

ScoreFilter.displayName = "ScoreFilter";
